import { HdBodygraphRenderStateFactory } from '../bodygraph/hd-bodygraph-render-state.factory';
import { HdBodygraphConfig, HdBodygraphPlanetaryActivation } from '../bodygraph/hd-bodygraph.models';
import {
  HdCycleCardSlot,
  HdCycleCardSource,
  HdCycleConditionedCenter,
  buildHdCycleChannelCardSources,
  buildHdCycleConditionedCenters,
  buildHdCycleHangingGateCardSources,
  sortByHdCyclePriority,
  toHdCycleCardSlots,
} from '../bodygraph/hd-cycle-cards.builder';
import {
  HdCycleChannelVariant,
  HdCycleChannelVm,
  HdCycleHangingGateVm,
  buildHdCycleKeynotes,
} from '../bodygraph/hd-cycle-keynotes.builder';
import { HdCycleMainKeynoteKey } from '../bodygraph/hd-cycles-cheat-sheet-metadata';
import { HD_CHIRON_DISPLAY_CONFIG, getHdPlanetDisplayConfigByPlanetId } from '../bodygraph/planet-grid/hd-planet-display-config';

/** De drie cheat-sheet-keynotes + de gedempte vernieuwings-sectie (zelfde indeling als het Geavanceerd-paneel). */
export type HdJustNowSectionKey = HdCycleMainKeynoteKey | 'renewal';

/** Volgorde van de secties in het paneel. */
export const HD_JUST_NOW_SECTION_ORDER: readonly HdJustNowSectionKey[] = ['personal', 'educational', 'environmental', 'renewal'];

export type HdJustNowItemVm =
  | {
      readonly kind: 'channel';
      readonly key: string;
      readonly channel: HdCycleChannelVm;
      readonly variant: HdCycleChannelVariant;
      readonly cards: readonly HdCycleCardSlot[];
    }
  | {
      readonly kind: 'gate';
      readonly key: string;
      readonly hanging: HdCycleHangingGateVm;
      readonly cards: readonly HdCycleCardSlot[];
    };

export interface HdJustNowSectionVm {
  readonly key: HdJustNowSectionKey;
  readonly items: readonly HdJustNowItemVm[];
}

export interface HdJustNowPanelModel {
  /** In cheat-sheet-volgorde, met per center de transitactivaties die het definiëren. */
  readonly conditionedCenters: readonly HdCycleConditionedCenter[];
  /** Enkel de niet-lege secties, in {@link HD_JUST_NOW_SECTION_ORDER}. */
  readonly sections: readonly HdJustNowSectionVm[];
  /** Aantal volledige kaarten (unieke transitactivaties die iets vormen of hangen). */
  readonly cardCount: number;
}

export interface HdJustNowPanelInput {
  readonly natalConfiguration: HdBodygraphConfig;
  readonly natalPlanetaryActivations: readonly HdBodygraphPlanetaryActivation[];
  /** Personality-activaties van het transitmoment. */
  readonly transitActivations: readonly HdBodygraphPlanetaryActivation[];
  readonly renderStateFactory: HdBodygraphRenderStateFactory;
}

/**
 * Bouwt het model van het Just Now-paneel: dezelfde indeling als het
 * Geavanceerd-paneel van een cycle-/momentchart ({@link buildHdCycleKeynotes}),
 * met per item de I Tjing-kaarten van de transitpoort(en)
 * (`hd-cycle-cards.builder`):
 *
 * - **Persoonlijk** (electromagnetic: één kaart, van de transitpoort;
 *   compromis: twee), **Educatief** (dominantie: twee), **Omgevend** (één per
 *   planeetactivatie op de losse poort), gedempt **Vernieuwing** achteraan.
 * - Binnen een sectie op planeetprioriteit (planetengrid-volgorde): een kanaal
 *   lift mee op zijn hoogste transitplaneet; de kaarten van een item in dezelfde
 *   volgorde.
 * - Een transitactivatie die in meerdere items voorkomt krijgt één volledige
 *   kaart (eerste voorkomen) en daarna verwijzingen.
 * - De niet-zelf centers: natal ongedefinieerd, in de combinatie gedefinieerd,
 *   met als oorzaken de transitactivaties op de (in de combinatie gevormde)
 *   kanalen die het center raken.
 *
 * Chiron doet nooit mee (hoort niet bij de chart, zoals in de cycle-builder).
 */
export function buildHdJustNowPanelModel(input: HdJustNowPanelInput): HdJustNowPanelModel {
  const transit = input.transitActivations.filter(isCardCandidate);
  const breakdown = buildHdCycleKeynotes(input.natalPlanetaryActivations, transit);

  type Prioritized = { readonly sources: readonly HdCycleCardSource[]; readonly build: (seen: Set<string>) => HdJustNowItemVm };
  const channelItem = (channel: HdCycleChannelVm): Prioritized => {
    const sources = buildHdCycleChannelCardSources(channel);
    return {
      sources,
      build: seen => ({
        kind: 'channel',
        key: `channel:${channel.channelKey}`,
        channel,
        variant: channel.variant,
        cards: toHdCycleCardSlots(sources, seen),
      }),
    };
  };
  const gateItem = (hanging: HdCycleHangingGateVm): Prioritized => {
    const sources = buildHdCycleHangingGateCardSources(hanging);
    return {
      sources,
      build: seen => ({
        kind: 'gate',
        key: `gate:${hanging.gate}`,
        hanging,
        cards: toHdCycleCardSlots(sources, seen),
      }),
    };
  };
  const itemsFor = (key: HdJustNowSectionKey): Prioritized[] => {
    switch (key) {
      case 'personal':
        return breakdown.personal.map(channelItem);
      case 'educational':
        return breakdown.educational.map(channelItem);
      case 'environmental':
        return breakdown.environmental.map(gateItem);
      default:
        return breakdown.renewals.map(channelItem);
    }
  };

  // Dedup over de hele render-volgorde: eerste voorkomen = volledige kaart.
  const seen = new Set<string>();
  const sections: HdJustNowSectionVm[] = [];
  for (const key of HD_JUST_NOW_SECTION_ORDER) {
    // Stabiel op planeetprioriteit, dan de volgorde van de builder (kanaallijst / poortnummer).
    const items = sortByHdCyclePriority(itemsFor(key), item => item.sources).map(item => item.build(seen));
    if (items.length > 0) {
      sections.push({ key, items });
    }
  }

  return {
    conditionedCenters: buildHdCycleConditionedCenters(input.natalConfiguration, transit, input.renderStateFactory),
    sections,
    cardCount: seen.size,
  };
}

function isCardCandidate(activation: HdBodygraphPlanetaryActivation): boolean {
  return activation.side === 'personality'
    && activation.planetId !== HD_CHIRON_DISPLAY_CONFIG.planetId
    && getHdPlanetDisplayConfigByPlanetId(activation.planetId) != null
    && Number.isInteger(activation.line) && activation.line >= 1 && activation.line <= 6;
}
